import { Image } from 'expo-image';
import React, { ReactNode } from 'react';
import { StyleSheet, View } from 'react-native';

import { optimizeSanityImageUri } from '../../utils';
import { QuantityCounter } from '../quantity';
import { XStack } from '../stack';
import { Text } from '../text';

import { IMAGE_HEIGHT } from './constants';
import { ListItemUnavailableBadge } from './list-item-unavailable-badge';
import { listStyles } from './shared-styles';
import type { ListItemType } from './types';

type ListItemQuantityProps = Omit<ListItemType, 'subtitle'> & {
  minValue: number;
  maxValue: number;
  disabled?: boolean;
  value: number;
  subtitle?: ReactNode;
  /** Passed to the stepper for VoiceOver (e.g. item name + additional cost). */
  label?: string;
  onChangeValue: (nextValue: number) => void;
};

export function ListItemQuantity({ minHeightStyle = 'static', ...props }: ListItemQuantityProps) {
  const cmsImageAlt = props.image?.altText?.trim();
  const subtitleLine =
    typeof props.subtitle === 'string' && props.subtitle.trim() ? props.subtitle.trim() : '';

  const unavailableRowLabel = props.disabled
    ? [
        props.title,
        subtitleLine || null,
        props.indicatorText?.trim() ? props.indicatorText.trim() : null,
        'Unavailable',
      ]
        .filter((s): s is string => typeof s === 'string' && s.trim().length > 0)
        .join('. ')
    : '';

  // Unavailable rows: one merged label on the outer View (no stepper inside).
  // Enabled rows: keep the outer View out of the accessibility tree so VoiceOver/TalkBack
  // can reach QuantityCounter.Stepper descendants; title/subtitle stay on their own nodes.
  const hideTitleSubtitleFromTree = props.disabled && Boolean(unavailableRowLabel);

  return (
    <View
      style={[
        listStyles.item,
        minHeightStyle === 'static' && listStyles.minHeightStyleStatic,
        props.isLastItem && listStyles.lastItem,
      ]}
      accessible={hideTitleSubtitleFromTree}
      accessibilityLabel={hideTitleSubtitleFromTree ? unavailableRowLabel : undefined}
    >
      {props.image?.asset?.uri && (
        <Image
          contentFit="contain"
          style={[listStyles.image, props.disabled && listStyles.imageDisabled]}
          accessibilityLabel={cmsImageAlt || props.title}
          alt={cmsImageAlt || props.title}
          accessible={false}
          accessibilityElementsHidden={props.disabled}
          source={{ uri: optimizeSanityImageUri(props.image.asset.uri, IMAGE_HEIGHT) }}
          placeholder={{ blurhash: props.image.asset.blurHash }}
        />
      )}
      <View style={listStyles.titleTextContainer}>
        <View>
          <Text.Ui
            accessible={!hideTitleSubtitleFromTree}
            importantForAccessibility={hideTitleSubtitleFromTree ? 'no' : undefined}
            weight={props.titleFontWeight ?? 'semibold'}
            size="md"
            numberOfLines={1}
            style={props.disabled && listStyles.textDisabled}
          >
            {props.title}
          </Text.Ui>
        </View>
        {Boolean(props.subtitle) && (
          <View>
            <Text.Ui
              accessible={!hideTitleSubtitleFromTree}
              importantForAccessibility={hideTitleSubtitleFromTree ? 'no' : undefined}
              size="sm"
              numberOfLines={1}
              style={listStyles.subtitle}
            >
              {props.subtitle}
            </Text.Ui>
          </View>
        )}
      </View>

      {props.disabled ? (
        <ListItemUnavailableBadge />
      ) : (
        <XStack style={listStyles.indicatorWithTextWrapper}>
          {Boolean(props.indicatorText) && (
            <Text.Ui size="sm" numberOfLines={1} style={listStyles.indicatorText}>
              {props.indicatorText}
            </Text.Ui>
          )}
          <QuantityCounter
            minValue={props.minValue}
            maxValue={props.maxValue}
            value={props.value}
            onChangeValue={props.onChangeValue}
            size="sm"
            label={props.label}
          >
            <QuantityCounter.Stepper style={styles.quantityCounter} />
          </QuantityCounter>
        </XStack>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  quantityCounter: {
    minWidth: 100,
    justifyContent: 'space-between',
  },
});
